import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import { api } from "../../../../services/api/api.service";
import {toastr} from "react-redux-toastr";
import {DartsPhase, IDartsPhase, IDartsPhaseCreatePayload} from "./types";
import { API_DARTS_PHASE } from "../../../../services/api/api.urls";
import { COMPANY_ABBR } from "../../../../common/constants/company";
import { TryCatch } from "../../../../services/TryCatch";

export interface IGetPhaseResponse {
    totalItems: 0,
    totalPages: 0,
    pageNum: 0,
    pageSize: 0,
    items: IDartsPhase[]
}

const initialState: DartsPhase = {
    phases: [] as IDartsPhase[],
    isLoading: true,
    isVisibleForm: false,
    editMode: false,
    editPhaseId: null,
    noContent: null,
    pageNum:1,
    pageSize:10,
    totalItems:0,
    totalPages:0
}

export const getDartsPhases = createAsyncThunk<IGetPhaseResponse, string, { rejectValue: string }>(
    'dartsPhases/get',
    async (query, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_DARTS_PHASE}?${query}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch series.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)


export const createDartsPhase = createAsyncThunk<IDartsPhaseCreatePayload, IDartsPhaseCreatePayload, { rejectValue: string }>(
    'dartsPhase/post',
    async (payload, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.post(API_DARTS_PHASE, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to create a series.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Darts series has been created");
        return result.data;
    }
)

export const editDartsPhase = createAsyncThunk<IDartsPhaseCreatePayload, IDartsPhaseCreatePayload, { rejectValue: string }>(
    'dartsPhase/edit',
    async (payload, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.put(`${API_DARTS_PHASE}`, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to edit series.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Darts phase has been edited");
        return result.data;
    }
)

export const deleteDartsPhase = createAsyncThunk<DartsPhase, number, { rejectValue: string }>(
    'dartsPhases/delete',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.delete(`${API_DARTS_PHASE}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to delete series.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Series has been deleted");
        return result.data;
    }
)


export const cmsPhasesSlice = createSlice({
    name: 'cmsDartsPhases',
    reducers: {
        showPhaseForm: (state) => {
            state.isVisibleForm = true
        },
        hidePhaseForm: (state) => {
            state.isVisibleForm = false;
            state.editMode = false;
        },
        setEditModePhaseForm: (state, action: PayloadAction<number | null>) => {
            state.editMode = true;
            state.isVisibleForm = true;
            state.editPhaseId = action.payload;
        },
        setPageNumber: (state, action) => {
            state.pageNum = action.payload
        },
        changePageSize: (state, action) => {
            state.pageSize = action.payload
        }
    },
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(getDartsPhases.fulfilled, (state, action) => {
                state.phases = action.payload.items;
                state.noContent = false;
                state.isLoading = false;
                state.totalPages = action.payload.totalPages
            })
            .addCase(getDartsPhases.rejected, (state) => {
                state.isLoading = false;

            })
            .addCase(deleteDartsPhase.pending, (state) => {
                state.isVisibleForm = false;
                state.isLoading = true;
            })
            .addCase(deleteDartsPhase.fulfilled, (state, action) => {
                if (action.payload !== undefined) {
                    state.phases = state.phases.filter(
                        (phase) => phase.id !== action.meta.arg
                    );
                }
                state.isLoading = false;

            })
            .addCase(deleteDartsPhase.rejected, (state) => {
                state.isLoading = false;

            })
            .addCase(createDartsPhase.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createDartsPhase.fulfilled, (state, action) => {
                state.phases = state.phases.concat(action.payload);
                state.isLoading = false;
                state.isVisibleForm = false;
            })
            .addCase(createDartsPhase.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(editDartsPhase.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(editDartsPhase.fulfilled, (state, action) => {
                state.phases = state.phases.map((phase) => {
                    if (phase.id === action.payload.id) {
                        phase = action.payload
                    }
                    return phase
                })
                state.editMode = false;
                state.isLoading = false;
                state.isVisibleForm = false;
            })
            .addCase(editDartsPhase.rejected, (state: DartsPhase) => {
                state.isLoading = false;
            });
    },
});

export const {
    showPhaseForm,
    hidePhaseForm,
    setEditModePhaseForm,
    setPageNumber,
    changePageSize
} = cmsPhasesSlice.actions
export default cmsPhasesSlice.reducer;

